// core/services/firebase/postService.ts
import { 
  collection, 
  doc, 
  addDoc,
  getDoc, 
  getDocs,
  query, 
  where, 
  orderBy,
  limit,
  deleteDoc,
  updateDoc,
  Timestamp
} from 'firebase/firestore';
import { db } from './config';
import { SocialPost } from '../../types/social';
import { socialHelper } from '../../utils/socialHelper';

export const postService = {
  /**
   * Create a new post
   */
  async createPost(post: Omit<SocialPost, 'id' | 'createdAt' | 'likesCount' | 'commentsCount'>): Promise<string> {
    const newPost = {
      ...post,
      category: post.category || 'story',
      tags: post.tags || [],
      likesCount: 0,
      commentsCount: 0,
      createdAt: Timestamp.now()
    };

    const docRef = await addDoc(collection(db, 'social_posts'), newPost);

    // Process mentions in content
    if (post.content) {
      socialHelper.processMentions(
        post.content,
        post.authorId,
        post.authorHandle,
        post.authorName,
        docRef.id,
        'post',
        post.authorAvatar
      ).catch(console.error);
    }

    return docRef.id;
  },

  /**
   * Get posts from a specific author
   */
  async getPostsByAuthor(authorId: string, maxPosts: number = 20): Promise<SocialPost[]> {
    const q = query(
      collection(db, 'social_posts'),
      where('authorId', '==', authorId),
      orderBy('createdAt', 'desc'),
      limit(maxPosts)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date()
    } as SocialPost));
  },

  /**
   * Get posts filtered by tag
   */
  async getPostsByTag(tag: string, maxPosts: number = 20): Promise<SocialPost[]> {
    const q = query(
      collection(db, 'social_posts'),
      where('tags', 'array-contains', tag),
      orderBy('createdAt', 'desc'),
      limit(maxPosts)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date()
    } as SocialPost));
  },

  /**
   * Get feed posts (all public posts, ordered by date)
   */
  async getFeedPosts(maxPosts: number = 50): Promise<SocialPost[]> {
    const q = query(
      collection(db, 'social_posts'),
      orderBy('createdAt', 'desc'),
      limit(maxPosts)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date()
    } as SocialPost));
  },

  /**
   * Get a single post by ID
   */
  async getPostById(postId: string): Promise<SocialPost | null> {
    const docRef = doc(db, 'social_posts', postId);
    const snapshot = await getDoc(docRef);
    
    if (!snapshot.exists()) return null;
    
    return {
      id: snapshot.id,
      ...snapshot.data(),
      createdAt: snapshot.data().createdAt?.toDate() || new Date()
    } as SocialPost;
  },

  /**
   * Delete a post
   */
  async deletePost(postId: string): Promise<void> {
    await deleteDoc(doc(db, 'social_posts', postId));
  },

  /**
   * Update post engagement (likes/comments count)
   */
  async updateEngagement(postId: string, updates: { likesCount?: number; commentsCount?: number }): Promise<void> {
    await updateDoc(doc(db, 'social_posts', postId), updates);
  }
};
